import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const NotificationContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function NotificationProvider({ children }) {
  const { isAuthenticated, user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  // Categorize notification based on title/message content
  const categorizeNotification = (notification) => {
    const title = (notification.title || '').toLowerCase();
    const message = (notification.message || '').toLowerCase();
    const combined = title + ' ' + message;
    
    if (combined.includes('payment') || combined.includes('fee') || combined.includes('₹')) {
      return 'payments';
    }
    if (combined.includes('application') || combined.includes('submitted') || combined.includes('approved') || combined.includes('rejected')) {
      return 'applications';
    }
    if (combined.includes('exam') || combined.includes('mcq') || combined.includes('viva') || combined.includes('osce') || combined.includes('score')) {
      return 'exams';
    }
    if (combined.includes('certificate') || combined.includes('issued') || combined.includes('credential')) {
      return 'certificates';
    }
    return 'system';
  };

  // Fetch notification preferences
  const fetchPreferences = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(res.data);
    } catch (err) {
      console.error('Failed to fetch notification preferences:', err);
    }
  }, [token]);

  // Update notification preferences
  const updatePreferences = useCallback(async (newPrefs) => {
    if (!token) return;
    try {
      const res = await axios.put(`${API}/auth/notification-preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(res.data.preferences);
      return res.data;
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      throw err;
    }
  }, [token]);

  // Build WebSocket URL
  const getWsUrl = useCallback(() => {
    if (!user?.id) return null;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    
    return `${wsProtocol}://${wsHost}/ws/notifications/${user.id}?token=${token}`;
  }, [user?.id, token]);

  // Fetch preferences when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchPreferences();
    }
  }, [isAuthenticated, token, fetchPreferences]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const wsUrl = getWsUrl();
    if (!wsUrl || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          
          // Skip connection confirmation
          if (notification.id?.startsWith('conn_')) return;
          
          // Add category based on notification type/title
          const category = categorizeNotification(notification);
          const enrichedNotification = { ...notification, category };
          
          // Add to notifications list
          setNotifications(prev => [enrichedNotification, ...prev.slice(0, 49)]);
          setUnreadCount(prev => prev + 1);
          
          // Show toast for important notifications
          if (notification.type === 'error' || notification.type === 'alert') {
            toast.error(notification.title, { description: notification.message });
          } else if (notification.type === 'warning') {
            toast.warning(notification.title, { description: notification.message });
          } else if (notification.type === 'success') {
            toast.success(notification.title, { description: notification.message });
          } else {
            toast.info(notification.title, { description: notification.message });
          }
        } catch (err) {
          console.error('Failed to parse notification:', err);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Reconnect with exponential backoff (max 30 seconds)
        if (isAuthenticated && reconnectAttempts.current < 10) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Attempting reconnect (${reconnectAttempts.current})...`);
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
    }
  }, [getWsUrl, isAuthenticated]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id && token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, user?.id, token, connect, disconnect]);

  // Send ping every 30 seconds to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isConnected]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Get notifications by category
  const getNotificationsByCategory = useCallback((category) => {
    if (!category || category === 'all') return notifications;
    return notifications.filter(n => n.category === category);
  }, [notifications]);

  // Get unread count by category
  const getUnreadCountByCategory = useCallback((category) => {
    if (!category || category === 'all') return unreadCount;
    return notifications.filter(n => n.category === category && !n.read).length;
  }, [notifications, unreadCount]);

  const value = {
    notifications,
    unreadCount,
    isConnected,
    preferences,
    markAsRead,
    markAllAsRead,
    clearAll,
    updatePreferences,
    getNotificationsByCategory,
    getUnreadCountByCategory,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
